import {useEffect, useState, useRef, useCallback} from 'react';
import {MCatChatStreamConversationRequest} from "../../../services/mcatChat/types";
import {useAppSelector} from "../../../hooks";
import {RootState, store} from "../../../state/store";
import {useAuthenticateUserMutation} from "../../../services/mcatChat/mcatChatExternal";
export const useMcatChatStreaming = () => {

    const [isLoading, setIsLoading] = useState(false);
    const [streamResponse, setStreamResponse] = useState('');
    const [error, setError] = useState<any>(null);
    const [temporaryRequestBody, setTemporaryRequestBody] = useState<MCatChatStreamConversationRequest | null>(null);
    const [currentMcatChatToken, setCurrentMcatChatToken] = useState<string | null | undefined>(null);
    const [prevMcatChatToken, setPrevMcatChatToken] = useState<string | null | undefined>(null);
    
    // Ref to store the current abort controller and reader for cleanup
    const abortControllerRef = useRef<AbortController | null>(null);
    const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

    const { mcatChatAuthInfo: {mcatChatToken} } = useAppSelector((state) => state.auth);

    //define the mutation for authenticate the user
    const [sendUserAuthRequest]: any = useAuthenticateUserMutation();
    
    // Cleanup function to cancel ongoing streams - memoized to prevent unnecessary re-renders
    const cancelStream = useCallback(() => {
        if (readerRef.current) {
            try {
                readerRef.current.cancel();
                readerRef.current = null;
            } catch (e) {
                console.error('Error canceling reader:', e);
            }
        }
        if (abortControllerRef.current) {
            try {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            } catch (e) {
                console.error('Error aborting fetch:', e);
            }
        }
        // Reset streaming state when cancelling to prevent stale data from interfering
        setStreamResponse('');
        setError(null);
        setIsLoading(false);
    }, []); // Empty deps - function only uses refs which are stable
    
    // use to reset the hook data - memoized since it's used in sendStreamMessage
    const resetHook = useCallback(() => {
        setStreamResponse('');
        setError(null);
        setPrevMcatChatToken(null);
        setCurrentMcatChatToken(null);
        setTemporaryRequestBody(null);
        setIsLoading(true);
    }, []); // Empty deps - only uses setState functions which are stable

    const sendStreamMessage = useCallback(async (chatRequestBody: MCatChatStreamConversationRequest) => {
        // Cancel any ongoing stream before starting a new one
        cancelStream();
        
        resetHook();
        const token = (store.getState() as RootState).auth.mcatChatAuthInfo.mcatChatToken;
        
        // Create a new abort controller for this request
        abortControllerRef.current = new AbortController();
        
        try {
            const response = await fetch(`${process.env.REACT_APP_MCAT_CHAT_BASE_API}/Api/AiEnrichment/ChatStreaming`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(chatRequestBody),
                signal: abortControllerRef.current.signal,
            });

            if (response.ok && response.body) {
                const reader = response.body.getReader();
                readerRef.current = reader; // Store reader reference for cleanup
                const decoder = new TextDecoder();

                let fullResponse = '';
                try {
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;

                        const chunk = decoder.decode(value, { stream: true });
                        fullResponse += chunk;
                        setStreamResponse(prev => prev + chunk);
                    }
                } catch (readError: any) {
                    // Handle abort gracefully
                    if (readError.name === 'AbortError') {
                        console.log('Stream reading aborted');
                        return;
                    }
                    throw readError;
                } finally {
                    // Clean up reader reference after reading is complete
                    readerRef.current = null;
                }
            } else {
                throw new Error(JSON.stringify(response.status));
            }
        } catch (err) {
            // Handle abort gracefully
            if (err instanceof DOMException && err.name === 'AbortError') {
                console.log('Fetch request aborted');
                return;
            }
            
            if (err instanceof Error && JSON.parse(err.message) === 401) {
                setTemporaryRequestBody(chatRequestBody);
                setPrevMcatChatToken(token);
                sendUserAuthRequest();
            } else {
                setError(err);
            }
        } finally {
            setIsLoading(false);
            // Clean up abort controller reference after request completes
            abortControllerRef.current = null;
        }
    }, [cancelStream, sendUserAuthRequest, resetHook]); // Memoize to prevent unnecessary re-renders

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cancelStream();
        };
    }, [cancelStream]); // cancelStream is now stable (memoized), safe to include

    // after the re authentication this failed call happened again
    useEffect(() => {
        if (temporaryRequestBody && currentMcatChatToken && prevMcatChatToken && prevMcatChatToken !== currentMcatChatToken) {
            sendStreamMessage(temporaryRequestBody);
        }
    }, [temporaryRequestBody, currentMcatChatToken, prevMcatChatToken, sendStreamMessage]) // Include sendStreamMessage since it's now memoized

    // setup the current mcat token if exists
    useEffect(() => {
        if (mcatChatToken) setCurrentMcatChatToken(mcatChatToken);
    }, [mcatChatToken]);

    return {
        sendUserMessage: sendStreamMessage,
        aiResponse: streamResponse,
        isLoadingChatBubble: isLoading,
        errorChatBubble: error,
        cancelStream: cancelStream // Expose cancel function for external cleanup
    };
}; 